import * as Sentry from "@sentry/browser";
import { FormikErrors, FormikTouched } from "formik";
import React, {
  ForwardedRef,
  forwardRef,
  SyntheticEvent,
  useCallback,
  useState,
} from "react";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { TextFormField } from "./text_form_field";
import { isEmail } from "../../lib/validation/string";
import "./guest_details.css";
import { useAnalytics } from "@/lib/analytics/analytics";
import { usePrevious } from "@/hooks/use_previous";
import { CheckoutStripeInput } from "@/offering/template_offering_checkout_stripe_base";
import { useValidateEmailOnSubmitOrderFeatureFlag } from "@/core/splitio";
import { handleValidateEmail } from "@/offering/api";

interface GuestDetailsInput {
  fullName: string;
  companyName: string;
  phoneNumber: string;
  email: string;
}

interface GuestDetailsProps {
  values: GuestDetailsInput;
  errors: FormikErrors<GuestDetailsInput>;
  touched: FormikTouched<GuestDetailsInput>;
  setFieldValue: (field: string, value: any) => void;
  onChangeEmail: (value?: string) => void;
  setFieldTouched?: (
    field: string
  ) => Promise<void> | Promise<FormikErrors<CheckoutStripeInput>>;
  errorFieldId?: keyof CheckoutStripeInput;
  onErrorTextRef?: (
    ref: HTMLElement | null,
    errorId: keyof GuestDetailsInput
  ) => void;
  onInputBorderRef?: (
    ref: HTMLElement | null,
    errorId: keyof GuestDetailsInput
  ) => void;
  emailError?: string;
  setEmailError?: (error?: string) => void;
}

export const GuestDetails = forwardRef(
  (props: GuestDetailsProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      values,
      touched,
      errors,
      setFieldValue,
      onChangeEmail,
      setFieldTouched,
      errorFieldId,
      onErrorTextRef,
      onInputBorderRef,
      emailError,
      setEmailError,
    } = props;
    const { fbt } = useI18n();
    const analytics = useAnalytics();
    const [email, setEmail] = useState<string>();
    const prevEmail = usePrevious(values.email);
    const validateEmailOnSubmitOrderFeatureFlag =
      useValidateEmailOnSubmitOrderFeatureFlag();

    const onChangeGuestEmail = useCallback(
      async (email: string) => {
        setFieldValue("email", email);
        const filledByBrowserAutofill = email.length - prevEmail.length > 1;

        if (filledByBrowserAutofill && email !== prevEmail) {
          await handleValidateEmail(
            email,
            validateEmailOnSubmitOrderFeatureFlag,
            setEmailError
          );

          Sentry.setUser({ email });
          analytics.identify("", {
            email,
            name: values.fullName,
            source: "WL",
          });
        }
      },
      [
        setFieldValue,
        prevEmail,
        validateEmailOnSubmitOrderFeatureFlag,
        setEmailError,
        analytics,
        values.fullName,
      ]
    );

    const onEmailFocusOut = useCallback(
      async (event: SyntheticEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        if (isEmail(value) && value !== email) {
          setEmail(value);
          onChangeEmail(value);

          await handleValidateEmail(
            value,
            validateEmailOnSubmitOrderFeatureFlag,
            setEmailError
          );
          Sentry.setUser({ email: value });
          analytics.identify("", {
            email: value,
            name: values.fullName,
            source: "WL",
          });
        }

        setFieldTouched?.("email");
      },
      [
        analytics,
        email,
        onChangeEmail,
        setEmailError,
        setFieldTouched,
        validateEmailOnSubmitOrderFeatureFlag,
        values.fullName,
      ]
    );

    return (
      <div className="guest-details">
        <h3 className="guest-details__title">{fbt("Your details")}</h3>
        <div className="row">
          <div
            className="col-12 col-lg-6 mb-4"
            ref={errorFieldId === "fullName" ? ref : undefined}
          >
            <TextFormField
              name="fullName"
              label={fbt("Full name")}
              value={values.fullName}
              onChange={(fullName) => setFieldValue("fullName", fullName)}
              error={touched.fullName && errors.fullName}
              onBlur={() => setFieldTouched?.("fullName")}
              errorRef={(ref) => onErrorTextRef?.(ref, "fullName")}
              inputRef={(ref) => onInputBorderRef?.(ref, "fullName")}
            />
          </div>
          <div
            className="col-12 col-lg-6 mb-4"
            ref={errorFieldId === "companyName" ? ref : undefined}
          >
            <TextFormField
              name="companyName"
              label={fbt("Company Name")}
              value={values.companyName}
              onChange={(companyName) =>
                setFieldValue("companyName", companyName)
              }
              error={touched.companyName && errors.companyName}
              onBlur={() => setFieldTouched?.("companyName")}
              errorRef={(ref) => onErrorTextRef?.(ref, "companyName")}
              inputRef={(ref) => onInputBorderRef?.(ref, "companyName")}
            />
          </div>
          <div
            className="col-12 col-lg-6 mb-4"
            ref={errorFieldId === "email" ? ref : undefined}
          >
            <TextFormField
              name="email"
              label={fbt("Email")}
              value={values.email}
              onChange={onChangeGuestEmail}
              error={(touched.email && errors.email) || emailError}
              onBlur={onEmailFocusOut}
              errorRef={(ref) => onErrorTextRef?.(ref, "email")}
              inputRef={(ref) => onInputBorderRef?.(ref, "email")}
            />
          </div>
          <div
            className="col-12 col-lg-6 mb-4"
            ref={errorFieldId === "phoneNumber" ? ref : undefined}
          >
            <TextFormField
              name="phoneNumber"
              label={fbt("Phone number")}
              value={values.phoneNumber}
              onChange={(phoneNumber) =>
                setFieldValue("phoneNumber", phoneNumber)
              }
              error={touched.phoneNumber && errors.phoneNumber}
              onBlur={() => setFieldTouched?.("phoneNumber")}
              errorRef={(ref) => onErrorTextRef?.(ref, "phoneNumber")}
              inputRef={(ref) => onInputBorderRef?.(ref, "phoneNumber")}
            />
          </div>
        </div>
      </div>
    );
  }
);
